import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Color style constants
const PRIMARY_COLOR = "#ffffff";
const ACCENT_COLOR = "#3d0707";

// Password requirement rules (regex)
const criteria = [
    { regex: /.{8,}/, message: "At least 8 characters" },
    { regex: /[A-Z]/, message: "An uppercase letter" },
    { regex: /[a-z]/, message: "A lowercase letter" },
    { regex: /[0-9]/, message: "A number" },
    { regex: /[^A-Za-z0-9]/, message: "A special character" },
];

// Password strength meter logic
function getPasswordStrength(password) {
    if (!password)
        return { label: "Enter a password", color: "#acadaf", score: 0, criteriaMet: [] };
    let met = criteria.filter(c => c.regex.test(password));
    let score = met.length;
    let color, label;
    if (score <= 2) {
        label = "Weak";
        color = "#dc2626";
    } else if (score === 3) {
        label = "Moderate";
        color = "#eab308";
    } else {
        label = "Strong";
        color = "#16a34a";
    }
    return { label, color, score, criteriaMet: met.map(c => c.message) };
}

// Utility to get a possibly fresh access token from localStorage/sessionStorage/cookie
function getAccessToken() {
    let access = localStorage.getItem("accessToken");
    if (access && access !== "undefined" && access !== "null") return access;
    access = sessionStorage.getItem("accessToken");
    if (access && access !== "undefined" && access !== "null") return access;
    return null;
}

const ResetPass = () => {
    const [password, setPassword] = useState("");
    const [rePassword, setRePassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showRePassword, setShowRePassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const passwordStrength = getPasswordStrength(password);
    const metCriteriaSet = new Set(passwordStrength.criteriaMet);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Validation
        if (!password) {
            setError("Please enter a new password.");
            return;
        }
        // Check all criteria 
        for (const c of criteria) {
            if (!c.regex.test(password)) {
                setError("Password does not meet all requirements.");
                return;
            }
        }
        if (password !== rePassword) {
            setError("Passwords do not match.");
            return;
        }

        // Try to get access token more robustly
        const accessToken = getAccessToken();
        if (!accessToken) {
            setError("User is not authenticated. Please log in again.");
            setTimeout(() => {
                navigate("/portal");
            }, 1500);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch("https://portal.lsb.go.ke/api/change_password/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    password,
                    re_password: rePassword
                }),
            });
            let data;
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            // Improved: Detect auth errors (token expired or invalid) and show better message
            if (!response.ok) {
                let errDetail = data.detail || data.error || "";
                if (
                    response.status === 401 ||
                    (errDetail && /token.*invalid|not authenticated|unauthorized/i.test(errDetail))
                ) {
                    setError("Session expired or invalid. Please log in again.");
                    setTimeout(() => {
                        localStorage.removeItem("access");
                        localStorage.removeItem("refresh");
                        sessionStorage.removeItem("access");
                        sessionStorage.removeItem("refresh");
                        navigate("/portal");
                    }, 1800);
                } else {
                    setError(
                        errDetail ||
                        "Failed to reset password. Please try again."
                    );
                }
                setLoading(false);
                return;
            }

            setSuccess("Password reset successful. Logging you out...");
            // Remove tokens, log out, then redirect
            setTimeout(() => {
                localStorage.removeItem("access");
                localStorage.removeItem("refresh");
                sessionStorage.removeItem("access");
                sessionStorage.removeItem("refresh");
                navigate("/portal");
            }, 2000);
        } catch (err) {
            setError("There was a network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[75vh] py-8" style={{ background: PRIMARY_COLOR }}>
            <div className="max-w-md w-full shadow-lg rounded-xl px-8 py-10 bg-white border border-gray-100">
                <h2 className="text-2xl font-bold mb-7 text-center" style={{ color: ACCENT_COLOR, letterSpacing: ".05em" }}>
                    Reset Your Account Password
                </h2>
                {error && (
                    <div className="mb-4 text-center text-red-600 text-sm font-medium">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-4 text-center text-green-700 text-sm font-medium">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* New Password */}
                    <div className="mb-6">
                        <label className="block font-semibold text-sm mb-1" htmlFor="newPassword" style={{ color: ACCENT_COLOR }}>
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="newPassword"
                                placeholder="Enter new password"
                                className="w-full rounded-lg border shadow-sm py-3 pl-4 pr-11 text-sm focus:outline-none"
                                style={{
                                    border: `1.5px solid ${PRIMARY_COLOR}`,
                                    background: "#fff8f3",
                                    color: ACCENT_COLOR,
                                    letterSpacing: ".05em"
                                }}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                            />
                            <button
                                type="button"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                onClick={() => setShowPassword(v => !v)}
                                className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#b27849] text-lg focus:outline-none"
                                tabIndex={-1}
                                style={{ background: "transparent" }}
                            >
                                {showPassword ? (
                                    <i className="fa-regular fa-eye" />
                                ) : (
                                    <i className="fa-regular fa-eye-slash" />
                                )}
                            </button>
                        </div>
                        {/* Password Strength Meter */}
                        <div className="mt-2">
                            <div
                                className="w-full h-2 rounded transition-all"
                                style={{
                                    background:
                                        passwordStrength.score === 0
                                            ? "#f3f4f6"
                                            : passwordStrength.color + "33",
                                }}
                            >
                                <div
                                    style={{
                                        height: "100%",
                                        width:
                                            passwordStrength.score === 0
                                                ? "0%"
                                                : `${passwordStrength.score * 20}%`,
                                        background: passwordStrength.color,
                                        borderRadius: 4,
                                        transition: "width 300ms",
                                    }}
                                />
                            </div>
                            <div
                                className="mt-1 text-md font-medium"
                                style={{
                                    color: passwordStrength.color
                                }}
                            >
                                {passwordStrength.label}
                            </div>
                        </div>
                        {/* Criteria checklist */}
                        <ul className="mt-1 text-xs">
                            {criteria.map((c) => (
                                <li
                                    key={c.message}
                                    style={{
                                        color: metCriteriaSet.has(c.message) ? "#16a34a" : "#b27849",
                                        fontWeight: metCriteriaSet.has(c.message) ? 600 : 400,
                                        letterSpacing: ".015em"
                                    }}
                                >
                                    {metCriteriaSet.has(c.message) ? "✓" : "✗"} {c.message}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Confirm Password */}
                    <div className="mb-7">
                        <label className="block font-semibold text-sm mb-1" htmlFor="rePassword" style={{ color: ACCENT_COLOR }}>
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showRePassword ? "text" : "password"}
                                id="rePassword"
                                placeholder="Confirm new password"
                                className="w-full rounded-lg border shadow-sm py-3 pl-4 pr-11 text-sm focus:outline-none"
                                style={{
                                    border: `1.5px solid ${PRIMARY_COLOR}`,
                                    background: "#fff8f3",
                                    color: ACCENT_COLOR,
                                    letterSpacing: ".05em"
                                }}
                                value={rePassword}
                                onChange={e => setRePassword(e.target.value)}
                                required
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                aria-label={showRePassword ? "Hide password" : "Show password"}
                                onClick={() => setShowRePassword(v => !v)}
                                className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#b27849] text-lg focus:outline-none"
                                tabIndex={-1}
                                style={{ background: "transparent" }}
                            >
                                {showRePassword ? (
                                    <i className="fa-regular fa-eye" />
                                ) : (
                                    <i className="fa-regular fa-eye-slash" />
                                )}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full rounded font-bold py-3 px-7 uppercase tracking-wide text-lg transition"
                        style={{
                            background: ACCENT_COLOR,
                            color: PRIMARY_COLOR,
                            boxShadow: "0 3px 18px #3d07071c",
                            letterSpacing: ".08em",
                            border: `2.5px solid ${PRIMARY_COLOR}`,
                            opacity: loading ? 0.7 : 1,
                        }}
                        disabled={loading}
                    >
                        {loading ? (
                            <span
                                className="inline-block align-middle"
                                style={{
                                    width: 24,
                                    height: 24,
                                    border: `3px solid ${PRIMARY_COLOR}`,
                                    borderTop: `3px solid ${ACCENT_COLOR}`,
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                }}
                            />
                        ) : (
                            "Set New Password"
                        )}
                    </button>
                </form>
            </div>
            <style>{`
                @media (max-width: 700px) {
                    .max-w-md { max-width: 98vw; }
                    .px-8 { padding-left: 1.3rem; padding-right: 1.3rem; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg);}
                    to { transform: rotate(360deg);}
                }
            `}</style>
        </div>
    );
};

export default ResetPass;